import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { CheckIcon, HeadphonesIcon, MapIcon, BookOpenIcon, AlertCircleIcon, SearchIcon, RefreshCwIcon } from 'lucide-react';
function GeoGrid() {
  return <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-265">
      {/* Primary grid */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: `
            linear-gradient(rgba(212,184,74,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212,184,74,0.3) 1px, transparent 1px)
          `,
      backgroundSize: '80px 80px'
    }} data-id="element-266" />
      {/* Secondary fine grid */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `
            linear-gradient(rgba(212,184,74,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212,184,74,0.5) 1px, transparent 1px)
          `,
      backgroundSize: '20px 20px'
    }} data-id="element-267" />
      {/* Radial vignette */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-dark-900/60 to-dark-900" data-id="element-268" />

      {/* Network connection nodes */}
      {[{
      top: '12%',
      left: '8%',
      size: 6,
      delay: 0
    }, {
      top: '25%',
      left: '85%',
      size: 4,
      delay: 0.5
    }, {
      top: '45%',
      left: '15%',
      size: 5,
      delay: 1
    }, {
      top: '60%',
      left: '92%',
      size: 3,
      delay: 1.5
    }, {
      top: '78%',
      left: '5%',
      size: 4,
      delay: 2
    }, {
      top: '35%',
      left: '50%',
      size: 3,
      delay: 0.8
    }, {
      top: '88%',
      left: '75%',
      size: 5,
      delay: 1.2
    }, {
      top: '15%',
      left: '45%',
      size: 3,
      delay: 0.3
    }, {
      top: '70%',
      left: '35%',
      size: 4,
      delay: 1.8
    }, {
      top: '50%',
      left: '70%',
      size: 3,
      delay: 0.6
    }].map((node, i) => <motion.div key={i} className="absolute rounded-full bg-gold-500" style={{
      top: node.top,
      left: node.left,
      width: node.size,
      height: node.size
    }} animate={{
      opacity: [0.08, 0.25, 0.08],
      scale: [1, 1.5, 1]
    }} transition={{
      duration: 4,
      delay: node.delay,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-269" />)}

      {/* Connection lines (SVG) */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" data-id="element-270">
        <line x1="8%" y1="12%" x2="45%" y2="15%" stroke="#d4b84a" strokeWidth="1" data-id="element-271" />
        <line x1="45%" y1="15%" x2="85%" y2="25%" stroke="#d4b84a" strokeWidth="1" data-id="element-272" />
        <line x1="15%" y1="45%" x2="50%" y2="35%" stroke="#d4b84a" strokeWidth="1" data-id="element-273" />
        <line x1="50%" y1="35%" x2="92%" y2="60%" stroke="#d4b84a" strokeWidth="1" data-id="element-274" />
        <line x1="5%" y1="78%" x2="35%" y2="70%" stroke="#d4b84a" strokeWidth="1" data-id="element-275" />
        <line x1="35%" y1="70%" x2="70%" y2="50%" stroke="#d4b84a" strokeWidth="1" data-id="element-276" />
        <line x1="70%" y1="50%" x2="75%" y2="88%" stroke="#d4b84a" strokeWidth="1" data-id="element-277" />
        <line x1="8%" y1="12%" x2="15%" y2="45%" stroke="#d4b84a" strokeWidth="0.5" strokeDasharray="4 6" data-id="element-278" />
        <line x1="85%" y1="25%" x2="92%" y2="60%" stroke="#d4b84a" strokeWidth="0.5" strokeDasharray="4 6" data-id="element-279" />
      </svg>

      {/* Coordinate markers */}
      <div className="absolute top-[10%] right-[6%] text-gold-500/10 font-mono text-[10px] tracking-widest" data-id="element-280">
        10.4806° N
      </div>
      <div className="absolute top-[10%] right-[6%] mt-4 text-gold-500/10 font-mono text-[10px] tracking-widest" data-id="element-281">
        66.9036° W
      </div>
      <div className="absolute bottom-[15%] left-[4%] text-gold-500/10 font-mono text-[10px] tracking-widest" data-id="element-282">
        8.9571° N
      </div>
      <div className="absolute bottom-[15%] left-[4%] mt-4 text-gold-500/10 font-mono text-[10px] tracking-widest" data-id="element-283">
        63.1733° W
      </div>
    </div>;
}
function FallbackForm() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Subscribe to Klaviyo list via their client-side API
      const klaviyo = (window as any)._klOnsite || (window as any).klaviyo;
      if (klaviyo && klaviyo.push) {
        klaviyo.push(['identify', {
          email
        }]);
      }
      // Also try the Klaviyo Forms API approach
      if ((window as any)._klOnsite) {
        ;
        (window as any)._klOnsite.push(['openForm', 'Vtq35Y']);
      }
      // Subscribe to Klaviyo list VCR7EN via Client API
      await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=Vtq35Y', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Bonos Tapa Blanda Landing Page',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: 'VCR7EN'
                }
              }
            }
          }
        })
      }).catch(() => {
        // Silent fail
      });
    } catch {
      // Silent fail
    }
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Scroll to Klaviyo form
    setTimeout(() => {
      const klaviyoDiv = document.getElementById('klaviyo-bonos');
      if (klaviyoDiv) {
        klaviyoDiv.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 300);
  };
  if (isSubmitted) {
    return <div className="text-center py-6 mt-4 border-t border-dark-700/50" data-id="element-284">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15
      }} className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-green-500 border border-green-500/30" data-id="element-285">
          <CheckIcon className="w-7 h-7" data-id="element-286" />
        </motion.div>
        <h3 className="text-xl font-serif font-bold text-white mb-1" data-id="element-287">
          ¡Revisa tu email!
        </h3>
        <p className="text-cream-200 text-sm" data-id="element-288">
          Te hemos enviado el acceso a tus bonos. Si no lo ves, revisa la
          carpeta de spam.
        </p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4 mt-4 border-t border-dark-700/50 pt-4" data-id="element-289">
      <div data-id="element-290">
        <label htmlFor="fallback-email" className="block text-xs font-mono font-medium text-cream-300 mb-1.5 tracking-wider uppercase" data-id="element-291">
          Email de compra
        </label>
        <input type="email" id="fallback-email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" className="w-full px-4 py-3.5 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-gold-500/40 focus:border-gold-500/60 transition-all font-mono text-sm" data-id="element-292" />
      </div>
      <motion.button type="submit" disabled={isSubmitting} whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.98
    }} className="w-full inline-flex items-center justify-center px-8 py-4 text-base font-bold tracking-wider transition-all duration-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-900 shadow-[0_0_25px_rgba(220,38,38,0.25)] hover:shadow-[0_0_35px_rgba(220,38,38,0.4)] disabled:opacity-70 disabled:cursor-not-allowed uppercase" style={{
      backgroundColor: '#dc2626',
      color: '#ffffff'
    }} data-id="element-293">
        {isSubmitting ? <span className="flex items-center gap-2" data-id="element-294">
            <motion.span animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" data-id="element-295" />
            Procesando...
          </span> : 'Obtener Mis Bonos'}
      </motion.button>
      <p className="text-[11px] text-cream-400/50 text-center leading-relaxed font-mono" data-id="element-296">
        Te enviaremos el acceso por email para que lo tengas guardado. Puedes
        darte de baja cuando quieras.
      </p>
    </form>;
}
function GoldDivider() {
  return <div className="relative flex items-center justify-center my-8 md:my-12" data-id="element-297">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" data-id="element-298" />
      <div className="mx-4 flex items-center gap-2" data-id="element-299">
        <div className="w-1.5 h-1.5 bg-gold-500/40 rotate-45" data-id="element-300" />
        <div className="w-2 h-2 border border-gold-500/30 rotate-45" data-id="element-301" />
        <div className="w-1.5 h-1.5 bg-gold-500/40 rotate-45" data-id="element-302" />
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" data-id="element-303" />
    </div>;
}
export function BonosTapaBlanda() {
  useEffect(() => {
    // Load Klaviyo script
    if (!document.querySelector('script[src*="klaviyo.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=Vtq35Y';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);
  return <main className="bg-dark-900 min-h-screen text-cream-100 selection:bg-gold-500/30 selection:text-gold-200 font-sans" data-id="element-304">
      <GeoGrid data-id="element-305" />

      <div className="relative z-10" data-id="element-306">
        {/* Top classified bar */}
        <div className="w-full py-2 bg-dark-800/80 border-b border-gold-500/10 backdrop-blur-sm" data-id="element-307">
          <p className="text-center text-[10px] font-mono tracking-[0.4em] text-gold-500/30 uppercase" data-id="element-308">
            Acceso Exclusivo · Minerales de Sangre · Edición Tapa Blanda
          </p>
        </div>

        {/* Hero Section */}
        <Section className="pt-12 md:pt-20 pb-12 md:pb-16" data-id="element-309">
          <div className="max-w-3xl mx-auto text-center" data-id="element-310">
            <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-311">
              <span className="inline-block px-4 py-1.5 rounded-sm bg-dark-800 border border-gold-500/30 text-gold-400 font-mono font-semibold text-[10px] tracking-[0.3em] uppercase mb-6 shadow-[0_0_15px_rgba(212,184,74,0.1)]" data-id="element-312">
                ◆ Acceso Exclusivo ◆
              </span>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-313">
                Reclama los materiales exclusivos de{' '}
                <span className="relative inline-block" data-id="element-314">
                  <span className="relative z-10" data-id="element-315">Minerales de Sangre</span>
                  <span className="absolute bottom-1 left-0 w-full h-2 bg-gold-500/15 -z-0" data-id="element-316" />
                </span>{' '}
                <span className="text-gold-400 italic" data-id="element-317">
                  (Libro Tapa blanda)
                </span>
              </h1>

              {/* Product Mockup */}
              <div className="relative flex justify-center my-8" data-id="element-318">
                <div className="relative" data-id="element-319">
                  <div className="absolute inset-0 bg-gold-500/15 blur-3xl rounded-full transform scale-75" data-id="element-320" />
                  <img src="https://cdn.magicpatterns.com/uploads/tHojTNV3t5LVj6hmUCDgUe/heylloyd-image-1772813457065.png" alt="Minerales de Sangre — Libro Tapa Blanda" className="relative z-10 w-full max-w-[280px] md:max-w-xs rounded-lg shadow-2xl shadow-black/60" data-id="element-321" />
                </div>
              </div>

              <p className="text-lg md:text-xl text-cream-200 mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-322">
                Tu compra incluye recursos adicionales para profundizar en los
                temas del libro de Minerales de Sangre edición tapa blanda. Deja
                tu email y te enviamos el acceso inmediato a tus bonos
                oficiales.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-start max-w-4xl mx-auto text-left" data-id="element-323">
              {/* Bullets */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative bg-dark-800/60 p-6 md:p-8 rounded-lg border border-dark-600/50 shadow-xl backdrop-blur-sm" data-id="element-324">
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-gold-500/30" data-id="element-325" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-gold-500/30" data-id="element-326" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-gold-500/30" data-id="element-327" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-gold-500/30" data-id="element-328" />

                <h3 className="text-sm font-mono font-bold text-gold-400 mb-6 uppercase tracking-[0.2em] border-b border-dark-600 pb-3 flex items-center gap-2" data-id="element-329">
                  <span className="w-2 h-2 bg-gold-500/60 rounded-full" data-id="element-330" />
                  Tus Bonos Incluyen
                </h3>
                <ul className="space-y-5" data-id="element-331">
                  <li className="flex items-start" data-id="element-332">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-500 mr-4 mt-0.5" data-id="element-333">
                      <HeadphonesIcon className="w-4 h-4" data-id="element-334" />
                    </div>
                    <div data-id="element-335">
                      <span className="font-bold text-cream-100 block" data-id="element-336">
                        Audiolibro (MP4)
                      </span>
                      <span className="text-sm text-cream-300/60" data-id="element-337">
                        Versión completa narrada
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-338">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-500 mr-4 mt-0.5" data-id="element-339">
                      <MapIcon className="w-4 h-4" data-id="element-340" />
                    </div>
                    <div data-id="element-341">
                      <span className="font-bold text-cream-100 block" data-id="element-342">
                        Mapa estratégico visual (PDF)
                      </span>
                      <span className="text-sm text-cream-300/60" data-id="element-343">
                        Guía de actores e intereses
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-344">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-500 mr-4 mt-0.5" data-id="element-345">
                      <BookOpenIcon className="w-4 h-4" data-id="element-346" />
                    </div>
                    <div data-id="element-347">
                      <span className="font-bold text-cream-100 block" data-id="element-348">
                        Glosario geopolítico esencial (PDF)
                      </span>
                      <span className="text-sm text-cream-300/60" data-id="element-349">
                        Definiciones clave para entender el contexto
                      </span>
                    </div>
                  </li>
                </ul>
              </motion.div>

              {/* Form */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="relative bg-dark-800 p-6 md:p-8 rounded-lg border border-gold-500/20 shadow-2xl shadow-gold-900/20 overflow-hidden" data-id="element-350">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-600 via-ember-500 to-gold-600" data-id="element-351" />
                {/* Corner accents */}
                <div className="absolute top-1 left-0 w-4 h-4 border-l border-gold-500/20" data-id="element-352" />
                <div className="absolute top-1 right-0 w-4 h-4 border-r border-gold-500/20" data-id="element-353" />

                <p className="text-[11px] text-cream-400/50 mb-4 text-center" data-id="element-354">
                  Ingresa el mismo correo electrónico que usaste en tu compra.
                </p>
                {/* Fallback form */}
                <FallbackForm data-id="element-355" />
                {/* Klaviyo embed — renders when triggered */}
                <div id="klaviyo-bonos" className="klaviyo-form-Vtq35Y mt-4" data-id="element-356"></div>
              </motion.div>
            </div>
          </div>
        </Section>

        <GoldDivider data-id="element-357" />

        {/* Troubleshooting Section */}
        <Section className="py-12 md:py-20" data-id="element-358">
          <div className="max-w-3xl mx-auto" data-id="element-359">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative bg-dark-800/40 p-8 md:p-10 rounded-lg border border-dark-700/60 text-center" data-id="element-360">
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-gold-500/20" data-id="element-361" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-gold-500/20" data-id="element-362" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-gold-500/20" data-id="element-363" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-gold-500/20" data-id="element-364" />

              <h2 className="text-2xl font-serif font-bold text-gold-400 mb-8 flex items-center justify-center gap-3" data-id="element-365">
                <AlertCircleIcon className="w-5 h-5" data-id="element-366" />
                Si no te llega el email…
              </h2>

              <div className="grid md:grid-cols-3 gap-8" data-id="element-367">
                {[{
                step: '01',
                icon: null,
                text: 'Revisa las carpetas de Spam, Promociones o Social en tu correo.'
              }, {
                step: '02',
                icon: SearchIcon,
                text: 'Busca el remitente o asunto "Minerales de Sangre" en tu bandeja.'
              }, {
                step: '03',
                icon: RefreshCwIcon,
                text: 'Si escribiste mal tu email, vuelve a registrarte con el correcto.'
              }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-368">
                    <div className="w-10 h-10 rounded bg-dark-900 flex items-center justify-center text-gold-400 mb-4 border border-dark-600 font-mono text-xs font-bold" data-id="element-369">
                      {item.icon ? <item.icon className="w-4 h-4" data-id="element-370" /> : item.step}
                    </div>
                    <p className="text-cream-200/80 text-sm leading-relaxed" data-id="element-371">
                      {item.text}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </Section>

        {/* Footer */}
        <footer className="py-8 border-t border-dark-800 text-center" data-id="element-372">
          <div className="max-w-4xl mx-auto px-6" data-id="element-373">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm text-cream-400/40" data-id="element-374">
              <span className="font-mono text-[11px] tracking-wider" data-id="element-375">
                © 2026 Minerales de Sangre
              </span>
              <span className="hidden md:inline text-gold-500/20" data-id="element-376">◆</span>
              <a href="#" className="hover:text-cream-300 transition-colors text-[11px] font-mono tracking-wider" data-id="element-377">
                Privacy Policy
              </a>
              <span className="hidden md:inline text-gold-500/20" data-id="element-378">◆</span>
              <a href="#" className="hover:text-cream-300 transition-colors text-[11px] font-mono tracking-wider" data-id="element-379">
                Terms of Service
              </a>
            </div>
          </div>
        </footer>
      </div>
    </main>;
}